// @ts-nocheck

import { flatten } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useReportingMap } from 'sonata-platform-components';
import { useDidUpdate } from 'sonata-react-components';

import { DataLayer } from '@/components/Map/components/DataLayer';
import { getCartographyLevels, getReportingStreamStatsUserId } from '@/modules/reporting/reporting.resources';
import { reportingToggleMapLoading } from '@/store/actions/reporting.actions';
import { getSelectedCampaigns } from '@/store/selectors/reporting.selectors';
import { getAdvancedFilters, getCampaigns, getCurrency } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';

function CartographyHeatmapLayer({
  countries,
  layerMapData,
  mapId,
  opacity = 0.5,
  section = 'general',
  show = true,
  showAllItems = false
}) {
  const dispatch = useDispatch();
  const { getMap, getMapCartographyData, getMapCartographyFilter, getMapEvents, setMapSettings } = useReportingMap();
  const map = getMap({ id: mapId });
  const { settings } = map;
  const cartographySettings = {
    ...settings.cartography,
    status: settings.cartography.status || 'idle'
  };
  const cartographySettingsCountries = settings.cartography.countries;
  const { level: shape } = cartographySettings;
  const cartographyFilter = getMapCartographyFilter({ id: mapId });
  const { level: cartographyLevel, shapeIds: cartographyLevelIds } = cartographyFilter;

  const generalFilters = useSelector(store => getAdvancedFilters(store, section));
  const currentCurrency = useSelector(store => getCurrency(store, section));
  const user = useSelector(getCurrentUser);
  const selectedCampaignIds = useSelector(getCampaigns);
  const reportingCampaigns = useSelector(getSelectedCampaigns);

  const tokens = {
    cartographyStats: {},
    countriesLevels: {},
    shapesNames: {}
  };

  const {
    cartographyLevel: generalSectionCartographyLevel,
    cartographyLevelIds: generalCartographyLevelIds,
    timeFrame,
    timeZone: timezone,
    variableType,
    variableValues
  } = generalFilters;

  const mapCartographyLevel = cartographyLevel || generalSectionCartographyLevel;
  const mapCartographyLevelIds = cartographyLevelIds.length ? cartographyLevelIds : generalCartographyLevelIds;
  const cartography = mapCartographyLevelIds.length ? { [mapCartographyLevel]: mapCartographyLevelIds } : undefined;
  const variable = variableType && variableValues.length ? { [variableType]: variableValues } : undefined;
  const groupLevel = cartographySettings.level ? `cartoPyramid.${cartographySettings.level}` : undefined;
  const mapEvents = getMapEvents({ id: mapId });

  const campaigns = reportingCampaigns.filter(reportingCampaign => selectedCampaignIds.includes(reportingCampaign.id));
  const getCartographyHeatmapBody = () => {
    if (groupLevel) {
      return {
        opts: {
          currencyOptions: {
            currency: currentCurrency
          }
        },
        params: {
          dateInterval: {
            timezone,
            ...timeFrame
          },
          filters: {
            cartography: {
              level0: cartographySettingsCountries,
              ...cartography
            },
            ...variable
          },
          groups: [groupLevel],
          outputMetrics: mapEvents
        }
      };
    }
  };

  const getLayerMapData = async () => {
    dispatch(reportingToggleMapLoading(mapId, true));
    const newCartographyStats = await getReportingStreamStatsUserId(
      getCartographyHeatmapBody(),
      tokens.cartographyStats,
      user.id,
      campaigns.map(campaign => campaign.id).join(','),
      'none',
      mapEvents
    );
    const itemsByCountry = await Promise.all(
      countries.map(country =>
        getCartographyLevels(
          cartographySettings.level,
          country,
          tokens.shapesNames,
          {
            [cartographySettings.level]: newCartographyStats.map(s => s[groupLevel])
          },
          ['boundingBox', 'description', 'name']
        )
      )
    );
    dispatch(reportingToggleMapLoading(mapId, false));
    setMapSettings({
      id: mapId,
      value: {
        ...settings,
        cartography: {
          ...settings.cartography,
          data: {
            ...settings.cartography.data,
            items: flatten(itemsByCountry),
            stats: newCartographyStats
          },
          status: 'idle'
        }
      }
    });
  };

  const mapData = layerMapData || getMapCartographyData({ id: mapId });

  useEffect(() => {
    return () => Object.values(tokens).forEach(token => token.cancel && token.cancel());
  }, []);

  useEffect(() => {
    if (layerMapData === undefined && !!groupLevel) {
      getLayerMapData();
    }
  }, [layerMapData, groupLevel]);

  useDidUpdate(() => {
    if (cartographySettings.status === 'reload') {
      getLayerMapData();
    }
  }, [cartographySettings.status]);

  useDidUpdate(() => {
    getLayerMapData();
  }, [mapEvents]);

  const tagFilters = showAllItems
    ? undefined
    : {
        [shape]: mapData.map(item => item._id)
      };

  return (
    show && (
      // biome-ignore lint/complexity/noUselessFragments: <explanation>
      <>
        {countries.map(country => (
          <DataLayer
            country={country}
            id={`${mapId}_cartographyHeatmap_${country}`}
            indexLimits={{
              max: 100,
              min: 0
            }}
            key={`${mapId}_cartographyHeatmap_${country}`}
            layerMapData={mapData}
            opacity={opacity}
            promoteId="name"
            shape={shape}
            type="vector"
            {...(tagFilters && { tagFilters })}
          />
        ))}
      </>
    )
  );
}

CartographyHeatmapLayer.propTypes = {
  countries: PropTypes.arrayOf(PropTypes.string).isRequired,
  layerMapData: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      w: PropTypes.number.isRequired
    })
  ),
  mapId: PropTypes.string.isRequired,
  opacity: PropTypes.number,
  section: PropTypes.string,
  show: PropTypes.bool,
  showAllItems: PropTypes.bool
};

export default CartographyHeatmapLayer;
